import { Button } from '@snitch/ui/button'
import { cn } from '@snitch/ui/lib/cn'
import { formatRub, plural } from '@snitch/ui/lib/format'
import { MotionValue } from '@snitch/ui/motion'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowUpRight, Check, ChevronDown, Copy, Globe, Send, Share2, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../../../lib/api.ts'
import { historyMoney } from '../../../lib/history.ts'
import { inTelegram, openExternalLink } from '../../../lib/telegram.ts'

export const Route = createFileRoute('/_authed/_shell/friends')({ component: Friends })
const day = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  timeZone: 'Europe/Moscow',
})
const card = 'rounded-[22px] border border-line bg-card p-5 md:p-[22px]'

function Friends() {
  const { me } = Route.useRouteContext()
  const overview = useQuery({
    queryKey: ['referrals', 'overview', me.user.id],
    queryFn: () => api.referrals.overview(),
  })
  const friends = useInfiniteQuery({
    queryKey: ['referrals', 'friends', me.user.id],
    initialPageParam: undefined as { id: string } | undefined,
    queryFn: ({ pageParam }) => api.referrals.list({ cursor: pageParam }),
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  })
  const [notice, setNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(null), 4500)
    return () => clearTimeout(timer)
  }, [notice])
  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value)
      setNotice(`${label} скопирован${label === 'Промокод' ? '' : 'а'}`)
    } catch {
      setNotice('Не удалось скопировать. Выделите текст и скопируйте его вручную.')
    }
  }
  async function share(url: string) {
    const percent = overview.data?.rules.welcomeDiscountPercent
    if (percent === undefined) return
    const message = `Приглашаю в Snitch — вам ${percent}% на первый обычный тариф`
    try {
      if (
        inTelegram &&
        openExternalLink(
          `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`,
        )
      )
        return
      if (navigator.share)
        await navigator.share({
          title: 'Приглашение в Snitch',
          text: message,
          url,
        })
      else {
        await navigator.clipboard.writeText(url)
        setNotice('Ссылка скопирована — отправьте её другу.')
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError'))
        setNotice('Не получилось поделиться. Попробуйте скопировать ссылку.')
    }
  }
  const data = overview.data
  const people = friends.data?.pages.flatMap((p) => p.items) ?? []
  return (
    <main className="flex flex-col gap-7 px-5 pt-7 pb-[130px] md:px-8 md:pb-9 xl:px-12 xl:pt-9">
      <header className="flex flex-col gap-2">
        <h1 className="font-bold text-[34px] tracking-[-0.6px]">Друзья</h1>
        <p className="text-ink-2 text-[15px]">Приглашайте по ссылке или личному промокоду</p>
      </header>
      {notice ? (
        <p
          role="status"
          className="flex items-center gap-2 rounded-2xl bg-sunken px-4 py-3 text-sm"
        >
          <Check className="size-4 shrink-0" />
          {notice}
        </p>
      ) : null}
      {overview.isError ? (
        <div role="alert" className={card}>
          <p>Не удалось загрузить приглашение.</p>
          <Button size="sm" className="mt-3" onClick={() => void overview.refetch()}>
            Повторить
          </Button>
        </div>
      ) : null}
      {!data && !overview.isError ? (
        <p role="status" className="text-ink-2">
          Загружаем приглашение…
        </p>
      ) : null}
      {data ? (
        <div className="grid gap-7 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
          <section
            aria-label="Бонусный баланс"
            className={cn(card, 'flex flex-col gap-[18px] xl:col-start-2 xl:row-start-1')}
          >
            <h2 className="text-ink-2 text-sm">Бонусный баланс</h2>
            <p className="font-semibold text-4xl tracking-[-1px]">
              <MotionValue>{historyMoney(data.bonusBalance)}</MotionValue>
            </p>
            <p className="text-ink-2 text-[13px]">Спишем при следующей покупке</p>
            <div className="grid grid-cols-2 gap-[18px] border-line border-t pt-[18px]">
              <div>
                <p className="font-semibold text-[23px]">{formatRub(data.earnedTotal)}</p>
                <p className="text-ink-2 text-xs">за приглашения</p>
              </div>
              <div>
                <p className="font-semibold text-[23px]">{data.friendCount}</p>
                <p className="text-ink-2 text-xs">
                  {plural(data.friendCount, ['друг', 'друга', 'друзей'])}
                </p>
              </div>
            </div>
            <Link
              to="/profile/history"
              search={{ view: 'bonuses' }}
              className="flex min-h-11 items-center justify-between font-semibold text-[15px]"
            >
              История бонусов
              <ArrowUpRight className="size-5" aria-hidden="true" />
            </Link>
          </section>
          <section
            className={cn(
              card,
              'flex min-w-0 flex-col gap-[18px] xl:col-start-1 xl:row-start-1 xl:row-span-2',
            )}
          >
            <h2 className="font-semibold text-[23px]">Ваше приглашение</h2>
            <p className="text-ink-2 text-[13px]">Личный промокод</p>
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 select-all break-all font-semibold text-[23px] tracking-[-0.6px] md:text-[27px]">
                {data.code}
              </p>
              <Button variant="soft" size="sm" onClick={() => void copy(data.code, 'Промокод')}>
                Скопировать
                <Copy className="size-4" aria-hidden="true" />
              </Button>
            </div>
            <p className="text-ink-2 text-[13px] leading-[1.4]">
              Друг может ввести код при регистрации или в профиле — до первого обычного тарифа.
            </p>
            <h3 className="border-line border-t pt-[18px] font-semibold text-[17px]">
              Пригласить по ссылке
            </h3>
            {[
              { label: 'На сайт', url: data.webUrl, Icon: Globe },
              { label: 'В Telegram-бота', url: data.botUrl, Icon: Send },
            ].map(({ label, url, Icon }, i) => (
              <div
                key={label}
                className={cn(
                  'flex min-w-0 items-center gap-3',
                  i > 0 && 'border-line border-t pt-[18px]',
                )}
              >
                <Icon className="size-[22px] shrink-0 text-blue" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-base">{label}</p>
                  <p className="select-all truncate text-ink-2 text-xs" title={url}>
                    {url.replace(/^https:\/\//, '')}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button
                    type="button"
                    aria-label={`Скопировать ссылку: ${label}`}
                    title={`Скопировать ссылку: ${label}`}
                    className="motion-button flex size-11 items-center justify-center rounded-full bg-sunken"
                    onClick={() => void copy(url, 'Ссылка')}
                  >
                    <Copy className="size-[18px]" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Поделиться ссылкой: ${label}`}
                    title={`Поделиться ссылкой: ${label}`}
                    className="motion-button flex size-11 items-center justify-center rounded-full bg-sunken"
                    onClick={() => void share(url)}
                  >
                    <Share2 className="size-[18px]" />
                  </button>
                </div>
              </div>
            ))}
          </section>
          <section
            className={cn(
              card,
              'flex flex-col gap-[18px] xl:col-start-2 xl:row-start-2 xl:row-span-2',
            )}
          >
            <h2 className="font-semibold text-[19px]">Как это работает</h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                [`${data.rules.firstPurchasePercent}%`, 'с первой оплаты тарифа'],
                [
                  `${data.rules.repeatPercent}%`,
                  data.rules.repeatWindowDays === 365
                    ? 'с дальнейших оплат за год'
                    : `с оплат за ${data.rules.repeatWindowDays} дней`,
                ],
                [`−${data.rules.welcomeDiscountPercent}%`, 'другу на первый тариф'],
              ].map(([percent, label]) => (
                <div key={percent}>
                  <p className="font-semibold text-[26px]">{percent}</p>
                  <p className="mt-1.5 text-ink-2 text-xs leading-[1.4]">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-ink-2 text-[13px] leading-[1.4]">
              Бонусы — с суммы, оплаченной деньгами. Тестовый тариф и списанные бонусы их не
              приносят.
            </p>
          </section>
          <section className="flex min-w-0 flex-col gap-3.5 xl:col-start-1 xl:row-start-3">
            <h2 className="flex items-center justify-between font-semibold text-[22px]">
              Ваши друзья
              <span className="font-normal text-ink-2 text-base">{data.friendCount}</span>
            </h2>
            {friends.isPending ? (
              <p role="status" className="text-ink-2 text-sm">
                Загружаем друзей…
              </p>
            ) : null}
            {people.length ? (
              <div className={cn(card, 'motion-list flex flex-col gap-[18px]')}>
                {people.map((person, i) => (
                  <div
                    key={person.id}
                    className={cn(
                      'flex items-center gap-3',
                      i > 0 && 'border-line border-t pt-[18px]',
                    )}
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-sunken font-semibold text-[15px]">
                      {person.name.charAt(0)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-base">{person.name}</p>
                      <p className="text-ink-2 text-xs">
                        {person.lastRewardAt ? 'Бонусы за покупки' : 'Приглашение принято'} ·{' '}
                        {day.format(person.lastRewardAt ?? person.acceptedAt)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 font-semibold text-[17px]',
                        person.earned > 0 ? 'text-green-ink' : 'text-ink-2',
                      )}
                    >
                      {person.earned > 0 ? '+' : ''}
                      {formatRub(person.earned)}
                    </span>
                  </div>
                ))}
              </div>
            ) : !friends.isPending && !friends.isError ? (
              <div className={cn(card, 'flex flex-col gap-3')}>
                <Users className="size-8 text-violet" aria-hidden="true" />
                <h3 className="font-semibold text-[21px]">Друзей пока нет</h3>
                <p className="text-ink-2 text-sm leading-[1.4]">
                  Поделитесь ссылкой на сайт, ссылкой на бота или промокодом. Здесь появятся друзья,
                  которые приняли приглашение.
                </p>
              </div>
            ) : null}
            {friends.isError ? (
              <div role="alert" className={card}>
                <p>Не удалось загрузить друзей.</p>
                <Button size="sm" className="mt-3" onClick={() => void friends.refetch()}>
                  Повторить
                </Button>
              </div>
            ) : null}
            {friends.hasNextPage ? (
              <Button
                variant="soft"
                block
                disabled={friends.isFetchingNextPage}
                onClick={() => void friends.fetchNextPage()}
              >
                {friends.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
                <ChevronDown aria-hidden="true" />
              </Button>
            ) : null}
          </section>
        </div>
      ) : null}
    </main>
  )
}
